import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Row, Col, Card, Typography, Button, Spin, Select, Divider } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useHistory, useParams } from 'react-router-dom'
import { constants } from '../../constants'
import moment from 'moment'
import { getPatient, getPatientDiagnoses, getBillingData } from '../../store/action/patient.action'
import { getDeviceSchedule } from '../../store/action/device.action'
import '../../css/patient.scss'

const { Title, Text } = Typography
const { Option } = Select

const PatientBilling = ({ patient, user, diagnoses, assignedDevices, billingData, ...props }) => {
  const history = useHistory()
  const { id: patientId } = useParams()
  const [loading, setLoading] = useState(false)
  
  // CPT qualification state - derived from billing data
  const cpt99453Qualifies = billingData?.data?.cpt99453?.qualifies || false
  const cpt99454Qualifies = billingData?.data?.cpt99454?.qualifies || false
  const cpt99457Qualifies = billingData?.data?.cpt99457?.qualifies || false
  
  // Date state - default to previous month
  const [selectedMonth, setSelectedMonth] = useState(() => {
    return moment().subtract(1, 'month').month()
  })
  const [selectedYear, setSelectedYear] = useState(() => {
    return moment().subtract(1, 'month').year()
  })

  // Access control - only COMPANY_ADMIN and COMPANY_HEALTHCOACH
  const hasAccess = user?.role == constants.ROLE.COMPANY_ADMIN || 
                   user?.role == constants.ROLE.COMPANY_HEALTHCOACH

  useEffect(() => {
    // Scroll to top when component mounts
    setTimeout(() => window.scrollTo(0, 0), 100)
    
    console.log('PatientBilling useEffect - patientId:', patientId, 'hasAccess:', hasAccess)
    
    // If no access, redirect back
    if (!hasAccess) {
      history.push('/patient')
      return
    }

    // Ensure we have a valid patientId before trying to load data
    if (!patientId) {
      console.error('No patientId provided')
      return
    }

    // Load patient data if not already loaded
    if (!patient || patient._id !== patientId) {
      console.log('Patient billing - loading patient data for:', patientId)
      props.getPatient(patientId)
    }

    // Load patient diagnoses
    if (patientId && (!diagnoses || !diagnoses.data)) {
      console.log('Patient billing - loading diagnoses for:', patientId)
      props.getPatientDiagnoses(patientId)
    }

    // Load device schedule
    if (patientId && (!assignedDevices || assignedDevices.length === 0)) {
      console.log('Patient billing - loading device schedule for:', patientId)
      props.getDeviceSchedule(patientId)
    }

    // Load billing data for selected month/year (only if not already loaded)
    if (patientId && selectedMonth !== undefined && selectedYear !== undefined && !billingData && !loading) {
      console.log('Patient billing - loading billing data for:', patientId, selectedMonth, selectedYear)
      setLoading(true)
      props.getBillingData(patientId, selectedMonth, selectedYear)
    }

    // Stop loading when billing data is received
    if (billingData && loading) {
      setLoading(false)
    }
    
    // Debug data
    console.log('BILLING DEBUG - Current diagnoses state:', diagnoses)
    console.log('BILLING DEBUG - Current assigned devices:', assignedDevices)
    console.log('BILLING DEBUG - Current billing data:', billingData)
    console.log('BILLING DEBUG - Loading state:', loading)
  }, [patientId, hasAccess, patient, diagnoses, assignedDevices, billingData, selectedMonth, selectedYear, loading])

  const handleBackToPatient = () => {
    history.push(`/patient/${patientId}`)
  }

  const handleMonthChange = (month) => {
    setSelectedMonth(month)
    // Reload billing data for new month
    if (patientId) {
      props.getBillingData(patientId, month, selectedYear)
    }
  }

  const handleYearChange = (year) => {
    setSelectedYear(year)
    // Reload billing data for new year
    if (patientId) {
      props.getBillingData(patientId, selectedMonth, year)
    }
  }

  // Generate month options
  const getMonthOptions = () => {
    const months = moment.months()
    const currentMoment = moment()
    
    return months.map((month, index) => {
      // Only allow current month and past months, not future
      const monthMoment = moment().year(selectedYear).month(index)
      const isDisabled = monthMoment.isAfter(currentMoment)
      
      return (
        <Option key={index} value={index} disabled={isDisabled}>
          {month}
        </Option>
      )
    })
  }

  // Generate year options (current year and past years)
  const getYearOptions = () => {
    const currentYear = moment().year()
    const years = []
    
    // Show current year and 5 years back
    for (let year = currentYear; year >= currentYear - 5; year--) {
      years.push(
        <Option key={year} value={year}>
          {year}
        </Option>
      )
    }
    
    return years
  }

  // Format the selected period for display
  const getSelectedPeriod = () => {
    return moment().month(selectedMonth).year(selectedYear).format('MMMM YYYY')
  }

  // Show access denied if user doesn't have permission
  if (!hasAccess) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Title level={4}>Access Denied</Title>
          <Text>You don't have permission to view billing information.</Text>
          <br /><br />
          <Button type="primary" onClick={() => history.push('/patient')}>
            Back to Patients
          </Button>
        </div>
      </Card>
    )
  }

  // Show loading if patient data is not available
  if (!patient || patient._id !== patientId) {
    return (
      <Card>
        <Spin spinning={true}>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Text>Loading patient information...</Text>
          </div>
        </Spin>
      </Card>
    )
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header with back button */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '20px' }}>
        <Col>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBackToPatient}
            style={{ marginRight: '16px' }}
          >
            Back to Patient
          </Button>
          <Title level={2} style={{ display: 'inline-block', margin: 0 }}>
            RPM Patient Report for {patient.firstname} {patient.lastname}
          </Title>
        </Col>
      </Row>

      {/* Date Selection */}
      <Card bordered={false} style={{ marginBottom: '20px' }}>
        <Row gutter={16} align="middle">
          <Col>
            <Text strong>Report Period:</Text>
          </Col>
          <Col>
            <Select
              value={selectedMonth}
              onChange={handleMonthChange}
              style={{ width: 120, marginRight: 8 }}
              placeholder="Month"
            >
              {getMonthOptions()}
            </Select>
          </Col>
          <Col>
            <Select
              value={selectedYear}
              onChange={handleYearChange}
              style={{ width: 100 }}
              placeholder="Year"
            >
              {getYearOptions()}
            </Select>
          </Col>
          <Col>
            <Text type="secondary">({getSelectedPeriod()})</Text>
          </Col>
        </Row>
      </Card>

      {/* Main content area */}
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          {/* Report Overview Section */}
          <Col span={24}>
            <Card title="Report Overview" bordered={false}>
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <Text strong>Report Period:</Text>
                  <br />
                  <Text>{getSelectedPeriod()}</Text>
                </Col>
                <Col span={6}>
                  <Text strong>Days with Data:</Text>
                  <br />
                  <Text>{billingData?.data?.cpt99454?.daysWithData || 0}</Text>
                </Col>
                <Col span={6}>
                  <Text strong>Total Review Time:</Text>
                  <br />
                  <Text>
                    {billingData?.data?.cpt99457?.totalTime 
                      ? `${Math.floor(billingData.data.cpt99457.totalTime / 60)}hrs ${billingData.data.cpt99457.totalTime % 60}mins`
                      : '0hrs 0mins'
                    }
                  </Text>
                </Col>
                <Col span={6}>
                  <Text strong>Billable CPT Codes:</Text>
                  <br />
                  <Text>
                    {[
                      cpt99453Qualifies ? '99453' : null,
                      cpt99454Qualifies ? '99454' : null, 
                      cpt99457Qualifies ? '99457' : null
                    ].filter(Boolean).join(', ') || 'None'}
                  </Text>
                </Col>
              </Row>
              <Divider />
              <Row>
                <Col span={24}>
                  <Text strong>Diagnoses:</Text>
                  <br />
                  {diagnoses?.data?.diagnoses && diagnoses.data.diagnoses.length > 0 ? (
                    diagnoses.data.diagnoses.map((diagnosis, index) => (
                      <Text key={index} style={{ display: 'block' }}>
                        <strong>{diagnosis.dxCode}</strong>: {diagnosis.dxDescription}
                      </Text>
                    ))
                  ) : (
                    <Text type="secondary">No diagnoses recorded</Text>
                  )}
                </Col>
              </Row>
            </Card>
          </Col>

          {/* Devices Overview Section - Only devices with captured data */}
          <Col span={24}>
            <Card title="Devices Overview" bordered={false}>
              {billingData?.data?.cpt99454?.measurementsByDevice && billingData.data.cpt99454.measurementsByDevice.length > 0 ? (
                <Row gutter={[16, 8]}>
                  {billingData.data.cpt99454.measurementsByDevice.map((device, index) => (
                    <Col span={12} key={index}>
                      <Card size="small" type="inner">
                        <Text strong>{device.device_name}</Text>
                        <br />
                        <Text type="secondary">HWI ID: {device.hwi_device_id}</Text>
                        <br />
                        <Text type="secondary">Measurements: {device.measurements?.length || 0}</Text>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Text type="secondary">No devices with captured data for this period</Text>
              )}
            </Card>
          </Col>

          {/* Event Log Section */}
          <Col span={24}>
            <Card title="Event Log" bordered={false}>
              {/* Only show CPT sections that qualify based on data */}
              
              {/* CPT 99453 Section - Only if device setup categories exist AND time > 20 min AND patient has devices */}
              {cpt99453Qualifies && (
                <Card 
                  size="small" 
                  title={`CPT 99453 - Device Setup (${Math.floor(billingData.data.cpt99453.totalTime / 60)}hrs ${billingData.data.cpt99453.totalTime % 60}mins)`}
                  style={{ marginBottom: '16px' }}
                  type="inner"
                >
                  <Text>
                    <strong>Date & Time:</strong> {moment(new Date(billingData.data.cpt99453.deviceSetupNotes.find(n => n.category === 'Onboarding Call Summary')?.datetime)).format('MM/DD/YYYY HH:mm A')}
                    <br />
                    <strong>Time Spent:</strong> {Math.floor(billingData.data.cpt99453.totalTime / 60)}hrs {billingData.data.cpt99453.totalTime % 60}mins
                    <br />
                    Patient has consented to be tracked using some or all of the following devices: {billingData.data.cpt99453.assignedDevices.map(d => d.display_name).join(', ')}.
                  </Text>
                  
                  {billingData.data.cpt99453.deviceSetupNotes.map((note, index) => (
                    <div key={index} style={{ marginTop: '12px', padding: '8px', backgroundColor: '#f5f5f5' }}>
                      <Text strong>{note.category}:</Text>
                      <br />
                      <Text>{note.notes}</Text>
                    </div>
                  ))}
                </Card>
              )}

              {/* CPT 99454 Section - Only if ≥16 days with measurements */}
              {cpt99454Qualifies && (
                <Card 
                  size="small" 
                  title={`CPT 99454 - Measurement Collection (${billingData.data.cpt99454.daysWithData} days)`}
                  style={{ marginBottom: '16px' }}
                  type="inner"
                >
                  {billingData.data.cpt99454.measurementsByDevice.map((device, index) => {
                    // For Tenovi Pulse Ox, only show spo2 measurements
                    const filteredMeasurements = device.device_name === 'Tenovi Pulse Ox' 
                      ? device.measurements.filter(m => m.metric === 'spo2')
                      : device.measurements
                    
                    const displayName = device.device_name === 'Tenovi Pulse Ox'
                      ? `${device.device_name} (spo2)`
                      : device.device_name
                    
                    return (
                      <div key={index} style={{ marginBottom: '16px' }}>
                        <Text strong>{displayName}</Text>
                        <br />
                        <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '8px' }}>
                          <table style={{ width: '100%', fontSize: '12px' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#f0f0f0' }}>
                                <th style={{ padding: '4px', border: '1px solid #ddd', width: '50%' }}>Timestamp</th>
                                <th style={{ padding: '4px', border: '1px solid #ddd', width: '50%' }}>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredMeasurements.slice(0, 50).map((measurement, mIndex) => (
                                <tr key={mIndex}>
                                  <td style={{ padding: '4px', border: '1px solid #ddd' }}>
                                    {moment(measurement.timestamp).format('DD/MM/YYYY HH:mm A')}
                                  </td>
                                  <td style={{ padding: '4px', border: '1px solid #ddd' }}>
                                    {measurement.value_2 > 0 
                                      ? `${measurement.value_1} / ${measurement.value_2}`
                                      : measurement.value_1
                                    }
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {filteredMeasurements.length > 50 && (
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              Showing first 50 of {filteredMeasurements.length} measurements
                            </Text>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </Card>
              )}

              {/* CPT 99457 Section - Only if total time_logged > 20 minutes */}
              {cpt99457Qualifies && (
                <Card 
                  size="small" 
                  title={`CPT 99457 - Patient Review Time (${Math.floor(billingData.data.cpt99457.totalTime / 60)}hrs ${billingData.data.cpt99457.totalTime % 60}mins)`}
                  style={{ marginBottom: '16px' }}
                  type="inner"
                >
                  {billingData.data.cpt99457.reviewNotes.map((note, index) => (
                    <div key={index} style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f5f5f5' }}>
                      <Row>
                        <Col span={6}>
                          <Text strong>{moment(new Date(note.datetime)).format('MM/DD/YYYY HH:mm A')}</Text>
                        </Col>
                        <Col span={18}>
                          <Text strong>{note.category}:</Text>
                          <br />
                          <Text>{note.notes}</Text>
                          <br />
                          <Text type="secondary">Time Spent: {Math.floor(note.time_logged / 60)}hrs {note.time_logged % 60}mins</Text>
                        </Col>
                      </Row>
                    </div>
                  ))}
                </Card>
              )}

              {/* Show message if no CPT codes qualify */}
              {!cpt99453Qualifies && !cpt99454Qualifies && !cpt99457Qualifies && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Text type="secondary">No billable activities found for this period</Text>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  )
}

PatientBilling.propTypes = {
  patient: PropTypes.object,
  user: PropTypes.object,
}

const mapStateToProps = (state) => ({
  patient: state.patient.patient,
  user: state.profile.user,
  diagnoses: state.patient.diagnoses,
  assignedDevices: state.device.assignedDevices,
  billingData: state.patient.billingData,
})

const mapDispatchToProps = (dispatch) => ({
  getPatient: (patientId) => dispatch(getPatient(patientId)),
  getPatientDiagnoses: (patientId) => dispatch(getPatientDiagnoses(patientId)),
  getDeviceSchedule: (patientId) => dispatch(getDeviceSchedule(patientId)),
  getBillingData: (patientId, month, year) => dispatch(getBillingData(patientId, month, year)),
})

export default connect(mapStateToProps, mapDispatchToProps)(PatientBilling)